import React, { useState } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ServerLogProvider } from './context/ServerLogContext';
import { ProgressProvider, useProgress } from './context/ProgressContext';
import CombinedRedirectManager from './components/CombinedRedirectManager';
import AllowlistManager from './components/AllowlistManager';
import ServerLogButton from './components/ServerLogButton';
import ProgressBar from './components/ProgressBar';
import './App.css';

// Protected content that is only visible to authenticated users
const ProtectedContent = () => {
  const { user } = useAuth();
  const [activePage, setActivePage] = useState('redirects');

  return (
    <div className="protected-content">
      <div className="navigation">
        <button 
          className={`nav-button ${activePage === 'redirects' ? 'active' : ''}`}
          onClick={() => setActivePage('redirects')}
        >
          Redirects
        </button>

        {user && user.userType === 'admin' && (
          <button 
            className={`nav-button ${activePage === 'allowlist' ? 'active' : ''}`}
            onClick={() => setActivePage('allowlist')}
          >
            Allowlist Management
          </button>
        )}
      </div>
      <div className="content">
        {activePage === 'redirects' ? (
          <CombinedRedirectManager />
        ) : (
          <AllowlistManager />
        )}
      </div>
      <ProgressBarWrapper />
    </div>
  );
};

// Progress bar wrapper component
const ProgressBarWrapper = () => {
  const { getAllOperations, clearCompletedOperations } = useProgress();
  const operations = getAllOperations();

  return (
    <ProgressBar 
      operations={operations}
      onClearCompleted={clearCompletedOperations}
    />
  );
};

// Login screen for unauthenticated users
const LoginScreen = () => {
  const { login, error } = useAuth();

  return (
    <div className="login-screen">
      <h2>Access Restricted</h2>
      <p>Please sign in with your Google Workspace account to access this site.</p>

      <div className="login-button-container">
        <button onClick={login} className="login-button">
          Sign in with Google
        </button>
      </div>

      {error && <p className="error-message">{error}</p>}
    </div>
  );
};

// Loading component
const LoadingScreen = () => (
  <div className="loading-screen">
    <h2>Loading...</h2>
  </div>
);

// Header component with logo, title and user info
const Header = () => {
  const { user, logout, isAuthenticated } = useAuth();

  return (
    <header className="App-header">
      <div className="header-left">
        <img src="/sauce-logo-300x54.png" alt="Sauce Logo" className="logo" />
        <h1>Redirect Manager</h1>
      </div>
      {isAuthenticated && user && (
        <div className="header-right">
          <div className="header-server-logs">
            <ServerLogButton />
          </div>
          {user.picture && (
            <img src={user.picture} alt="User Avatar" className="user-avatar" />
          )}
          <span className="user-name">{user.name}</span>
          <button onClick={logout} className="header-logout-button">Sign Out</button>
        </div>
      )}
    </header>
  );
};

// Main app component
const AppContent = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  return isAuthenticated ? <ProtectedContent /> : <LoginScreen />;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ServerLogProvider>
          <ProgressProvider>
            <Header />
            <main>
              <AppContent />
            </main>
            <footer>
              <p>By Sauce for Sauce.</p>
            </footer>
          </ProgressProvider>
        </ServerLogProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
